///
/// EarthBrowser - Bottom Drawer
///
/// Slide-up drawer that docks windows as tabs: the phone variant of the
/// data dock (a full-width detent sheet). Desktop uses RightDock.
/// Created by Matt Giger on December 28, 2025
/// Copyright (c) 2025 Geodata Labs
///

import {
	closeDrawerTab,
	reorderDrawerTabs,
	setActiveDrawerTab,
	setDrawerHeight,
	toggleDrawer,
	undockTabToWindow,
	type DatasetBrowserWindowState,
} from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { useIsMobile } from "@/components/utils/use-mobile"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { DrawerTab } from "./DrawerTab"
import { useDockKeyboardShortcuts } from "./useDockKeyboardShortcuts"
import { useDockWindowVisibility } from "./useDockWindowVisibility"
import { useWindowContent } from "./WindowContentRegistry"

// Constants
const MIN_DRAWER_HEIGHT = 200
const MAX_DRAWER_HEIGHT_RATIO = 0.7 // 70% of viewport
const RESIZE_HANDLE_HEIGHT = 6

// Mobile detent sheet: drag snaps to these fractions of the viewport height
const MOBILE_DETENTS = [0.35, 0.6, 0.9]
const MOBILE_MAX_HEIGHT_RATIO = 0.9

export interface BottomDrawerProps {
	// Optional function to get icon for window type
	getWindowIcon?: (windowType?: string) => { icon: ReactNode; color: string } | null
}

export function BottomDrawer({ getWindowIcon }: BottomDrawerProps) {
	const dispatch = useAppDispatch()
	const isMobile = useIsMobile()
	const { isOpen, height, tabs, activeTabId } = useAppSelector((state) => state.dialog.bottomDrawer)
	const datasetWindows = useAppSelector((state) => state.dialog.datasetWindows)

	// Globe-visibility toggles per tab (shared with the right dock)
	const { getVisibility: getWindowVisibility, getToggle: createToggleHandler } = useDockWindowVisibility()
	useDockKeyboardShortcuts()

	// Resize state
	const [isResizing, setIsResizing] = useState(false)
	const [resizeStartY, setResizeStartY] = useState(0)
	const [resizeStartHeight, setResizeStartHeight] = useState(0)
	const drawerRef = useRef<HTMLDivElement>(null)
	const didDragRef = useRef(false)

	// Tab reordering state (local-only during drag; commit to Redux on drop)
	const tabStripRef = useRef<HTMLDivElement>(null)
	const tabElByIdRef = useRef<Record<string, HTMLDivElement | null>>({})
	const [reorderTabIds, setReorderTabIds] = useState<string[]>([])
	const reorderFromIndexRef = useRef<number | null>(null)
	const reorderActiveIdRef = useRef<string | null>(null)

	// Get the active window
	const activeWindow = activeTabId ? datasetWindows[activeTabId] : null

	// Get content for active window (reactive to registry changes)
	const activeContent = useWindowContent(activeWindow)

	const reduxTabIds = useMemo<string[]>(() => tabs.map((t) => t.windowId), [tabs])

	// Keep local reorder list in sync when not actively dragging
	useEffect(() => {
		if (reorderActiveIdRef.current) return
		setReorderTabIds(reduxTabIds)
	}, [reduxTabIds])

	const effectiveTabIds = reorderTabIds.length > 0 ? reorderTabIds : reduxTabIds

	// Get all docked windows in the effective order
	const dockedWindows = effectiveTabIds
		.map((windowId: string) => datasetWindows[windowId])
		.filter((w: DatasetBrowserWindowState | undefined): w is DatasetBrowserWindowState => w !== undefined)

	// Handle toggle
	const handleToggle = useCallback(() => {
		dispatch(toggleDrawer())
	}, [dispatch])

	// Handle tab click - also opens drawer if collapsed
	const handleTabClick = useCallback((windowId: string) => {
		dispatch(setActiveDrawerTab(windowId))
		// If drawer is collapsed, expand it
		if (!isOpen) {
			dispatch(toggleDrawer())
		}
	}, [dispatch, isOpen])

	// Handle tab close
	const handleTabClose = useCallback((windowId: string) => {
		dispatch(closeDrawerTab(windowId))
	}, [dispatch])

	// Handle tab undock (drag out)
	const handleTabUndock = useCallback((windowId: string, x: number, y: number) => {
		dispatch(undockTabToWindow({ windowId, x, y }))
	}, [dispatch])

	const registerTabElement = useCallback((windowId: string, el: HTMLDivElement | null) => {
		tabElByIdRef.current[windowId] = el
	}, [])

	const handleReorderDragStart = useCallback((windowId: string) => {
		// Only allow reorder when there are at least 2 tabs
		if (reduxTabIds.length < 2) return
		reorderActiveIdRef.current = windowId
		reorderFromIndexRef.current = reduxTabIds.indexOf(windowId)
		setReorderTabIds(reduxTabIds)
	}, [reduxTabIds])

	const handleReorderDragMove = useCallback((windowId: string, clientX: number, _clientY: number, isUndocking: boolean) => {
		// Don't reorder if user is dragging out to undock
		if (isUndocking) return
		if (reorderActiveIdRef.current !== windowId) return
		if (reorderTabIds.length < 2) return

		const idsWithout = reorderTabIds.filter(id => id !== windowId)
		if (idsWithout.length === 0) return

		let insertAt = idsWithout.length
		for (let i = 0; i < idsWithout.length; i++) {
			const id = idsWithout[i]
			const el = tabElByIdRef.current[id]
			if (!el) continue
			const rect = el.getBoundingClientRect()
			const centerX = rect.left + rect.width / 2
			if (clientX < centerX) {
				insertAt = i
				break
			}
		}

		const next = [
			...idsWithout.slice(0, insertAt),
			windowId,
			...idsWithout.slice(insertAt),
		]

		// Avoid state churn if order didn't change
		const currentIndex = reorderTabIds.indexOf(windowId)
		if (currentIndex === insertAt) return
		setReorderTabIds(next)
	}, [reorderTabIds])

	const handleReorderDragEnd = useCallback((windowId: string, didUndock: boolean) => {
		if (reorderActiveIdRef.current !== windowId) return

		const fromIndex = reorderFromIndexRef.current
		const toIndex = reorderTabIds.indexOf(windowId)

		reorderActiveIdRef.current = null
		reorderFromIndexRef.current = null

		// If it undocked, don't reorder tabs (tab is leaving anyway)
		if (didUndock) {
			setReorderTabIds(reduxTabIds)
			return
		}

		if (fromIndex === null || fromIndex < 0 || toIndex < 0) {
			setReorderTabIds(reduxTabIds)
			return
		}

		if (fromIndex !== toIndex) {
			dispatch(reorderDrawerTabs({ fromIndex, toIndex }))
		}

		// Snap back to Redux order after commit
		setReorderTabIds(reduxTabIds)
	}, [dispatch, reduxTabIds, reorderTabIds])

	// Handle resize start
	const handleResizeStart = useCallback((e: React.PointerEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsResizing(true)
		setResizeStartY(e.clientY)
		setResizeStartHeight(height)
		didDragRef.current = false

		if (e.pointerType === "touch") {
			(e.target as HTMLElement).setPointerCapture(e.pointerId)
		}
	}, [height])

	// Handle resize move
	const handleResizeMove = useCallback((e: PointerEvent) => {
		if (!isResizing) return

		const deltaY = resizeStartY - e.clientY

		// Track if there was significant movement (more than 5px)
		if (Math.abs(deltaY) > 5) {
			didDragRef.current = true
		}

		const newHeight = resizeStartHeight + deltaY
		const maxRatio = isMobile ? MOBILE_MAX_HEIGHT_RATIO : MAX_DRAWER_HEIGHT_RATIO
		const maxHeight = window.innerHeight * maxRatio

		dispatch(setDrawerHeight(Math.max(MIN_DRAWER_HEIGHT, Math.min(newHeight, maxHeight))))
	}, [isResizing, resizeStartY, resizeStartHeight, dispatch, isMobile])

	// Handle resize end
	const handleResizeEnd = useCallback((e?: PointerEvent) => {
		if (e?.pointerType === "touch") {
			(e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId)
		}

		// If there was no significant drag movement, treat as a click to toggle
		if (!didDragRef.current) {
			dispatch(toggleDrawer())
		} else if (isMobile) {
			// Detent sheet behavior: snap the dragged height to the nearest detent
			setIsResizing(false)
			const current = drawerRef.current?.getBoundingClientRect().height
			if (current !== undefined) {
				const nearest = MOBILE_DETENTS
					.map(fraction => fraction * window.innerHeight)
					.reduce((best, detent) =>
						Math.abs(detent - current) < Math.abs(best - current) ? detent : best
					)
				dispatch(setDrawerHeight(Math.max(MIN_DRAWER_HEIGHT, nearest)))
			}
			return
		}

		setIsResizing(false)
	}, [dispatch, isMobile])

	// Set up resize event listeners
	useEffect(() => {
		if (isResizing) {
			document.addEventListener("pointermove", handleResizeMove)
			document.addEventListener("pointerup", handleResizeEnd)

			return () => {
				document.removeEventListener("pointermove", handleResizeMove)
				document.removeEventListener("pointerup", handleResizeEnd)
			}
		}
	}, [isResizing, handleResizeMove, handleResizeEnd])

	// Don't render if no tabs
	if (tabs.length === 0) {
		return null
	}

	return (
		<div
			ref={drawerRef}
			className={cn(
				"fixed bottom-0 left-0 right-0 z-[950]",
				"bg-black/30 backdrop-blur-xl",
				"border-t-2 border-slate-400/50",
				"rounded-t-2xl shadow-2xl",
				"transition-transform duration-300 ease-out",
				isOpen ? "translate-y-0" : "translate-y-[calc(100%-40px-var(--safe-area-inset-bottom))]"
			)}
			style={{
				// The home-indicator pad is added on top of the content height
				// (border-box would otherwise squeeze the 40px tab strip to
				// nothing on a phone); the collapsed translate keeps the strip
				// plus the pad visible
				height: isMobile ? `calc(${isOpen ? height : 40}px + var(--safe-area-inset-bottom))` : (isOpen ? height : 40),
				paddingBottom: isMobile ? "var(--safe-area-inset-bottom)" : undefined,
			}}
		>
			{/* Resize handle */}
			<div
				className={cn(
					"absolute top-0 left-0 right-0 flex items-center justify-center",
					"cursor-ns-resize select-none touch-none",
					isResizing && "bg-cyan-500/10"
				)}
				style={{ height: isMobile ? 14 : RESIZE_HANDLE_HEIGHT }}
				onPointerDown={handleResizeStart}
			>
				<div className={cn(
					"rounded-full bg-slate-500/50 hover:bg-slate-400/50 transition-colors",
					isMobile ? "w-9 h-1" : "w-12 h-1"
				)} />
			</div>

			{/* Tab bar */}
			<div className="flex items-center h-10 px-2 border-b border-slate-400/30">
				{/* Toggle button */}
				<button
					onClick={handleToggle}
					className="p-1.5 mr-2 rounded-full hover:bg-white/10 transition-all duration-300"
					aria-label={isOpen ? "Collapse drawer" : "Expand drawer"}
				>
					{isOpen ? (
						<ChevronDown className="w-4 h-4 text-white/60 hover:text-white" />
					) : (
						<ChevronUp className="w-4 h-4 text-white/60 hover:text-white" />
					)}
				</button>

				{/* Tabs */}
				<div
					ref={tabStripRef}
					className="flex-1 flex items-center gap-1 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-600"
				>
					{dockedWindows.map((window: DatasetBrowserWindowState) => (
						<DrawerTab
							key={window.id}
							window={window}
							isActive={window.id === activeTabId}
							onClick={() => handleTabClick(window.id)}
							onClose={() => handleTabClose(window.id)}
							onUndock={handleTabUndock}
							getWindowIcon={getWindowIcon}
							isVisible={getWindowVisibility(window)}
							onToggleVisibility={createToggleHandler(window)}
							registerTabElement={registerTabElement}
							onReorderDragStart={handleReorderDragStart}
							onReorderDragMove={handleReorderDragMove}
							onReorderDragEnd={handleReorderDragEnd}
						/>
					))}
				</div>
			</div>

			{/* Content area */}
			<div
				className={cn(
					"overflow-hidden",
					!isOpen && "hidden"
				)}
				style={{ height: isOpen ? height - 40 : 0 }}
			>
				{activeWindow && (
					// Keyed by window id so switching tabs remounts the content.
					// Without this React reconciles two tabs rendering the same
					// component type into one instance and mixes their state.
					<div key={activeWindow.id} className="h-full overflow-auto">
						{activeContent}
					</div>
				)}
			</div>
		</div>
	)
}

